import _ from 'lodash';
import { Store } from 'redux';
import watch from 'redux-watch';

import { AppMode } from 'uf/app/modes';
import { getActiveProjectId } from 'uf/app/selectors';
import { parseFullPath } from 'uf/base/dataset';
import { BUILT_FORM_TYPES } from 'uf/builtforms';
import {
  setBuiltFormEditorRoute,
  setBuiltFormEditorWithKeyRoute,
} from 'uf/builtforms/actions';
import { setEditorCurrentBuiltForm } from 'uf/builtforms/actions/editor';
import { makeGetEditorCurrentBuiltFormForProject } from 'uf/builtforms/selectors';
import { setExploreNewRoute } from 'uf/explore/actions/routing';
import { getExploreUIPropertiesState } from 'uf/explore/selectors/explore';
import { setSettingsRoute } from 'uf/organizations/actions/routing';
import { makeGetProjectBuiltFormsLibraryId } from 'uf/projects/selectors';
import { setReportRoute } from 'uf/reporting/actions/routing';
import { SyncStateWithUrl } from 'uf/routing/state';
import { setProfileRoute } from 'uf/user/actions/routing';

const getLibraryId = makeGetProjectBuiltFormsLibraryId();
export const syncAppUIStateWithBrowserUrl: SyncStateWithUrl = (
  store: Store,
) => {
  const watchAppUIState = watch(() => ({
    exploreUIProperties: getExploreUIPropertiesState(store.getState()),
    activeProjectId: getActiveProjectId(store.getState()),
  }));
  const getEditorCurrentBuiltFormForProject =
    makeGetEditorCurrentBuiltFormForProject();
  const unsubscribe = store.subscribe(
    watchAppUIState((newState, oldState) => {
      const {
        activeProjectId: newActiveProjectId,
        exploreUIProperties: newExploreUIProperties,
      } = newState;
      const {
        activeProjectId: oldActiveProjectId,
        exploreUIProperties: oldExploreUIProperties,
      } = oldState;

      // Make sure we are changing modebar route in case this
      // slice of state was changed for some other reason.
      // This might be a sign we need better redux architecture for these states.
      if (
        (_.isEqual(newExploreUIProperties, oldExploreUIProperties) &&
          newActiveProjectId === oldActiveProjectId) ||
        !newActiveProjectId
      ) {
        return;
      }

      let mode: AppMode;
      if (newExploreUIProperties.analysisUIVisible) {
        mode = AppMode.Analyze;
      }

      if (newExploreUIProperties.paintUIVisible) {
        mode = AppMode.Paint;
      }

      const state = store.getState();

      if (newExploreUIProperties.exploreViewVisible) {
        const { namespace, key } = parseFullPath(newActiveProjectId, 'project');
        store.dispatch(setExploreNewRoute(namespace, key, false, false, mode));
      } else if (newExploreUIProperties.reportViewVisible) {
        // TODO: get the key from redux so we remember reporting state!
        const reportSpecKey = 'paint_tracking';
        store.dispatch(setReportRoute(reportSpecKey, newActiveProjectId, []));
      } else if (newExploreUIProperties.manageViewVisible) {
        const currentBuiltForm = getEditorCurrentBuiltFormForProject(state, {
          projectId: newActiveProjectId,
        });
        if (!_.isEmpty(currentBuiltForm)) {
          // set current built form if it exists
          const { builtFormKey, builtFormType, libraryId } = currentBuiltForm;
          const { namespace, key } = parseFullPath(libraryId, 'builtforms');
          if (builtFormKey) {
            store.dispatch(
              setBuiltFormEditorWithKeyRoute(
                builtFormType,
                namespace,
                key,
                builtFormKey,
              ),
            );
          } else {
            store.dispatch(
              setBuiltFormEditorRoute(builtFormType, namespace, key),
            );
          }
        } else {
          // otherwise set what we know and let the component figure out the
          // builtform key from the list
          const { namespace, key } = parseFullPath(newActiveProjectId);
          const libraryId = getLibraryId(state, {
            projectId: newActiveProjectId,
          });
          store.dispatch(
            setEditorCurrentBuiltForm(
              newActiveProjectId,
              libraryId,
              BUILT_FORM_TYPES.BUILDING,
            ),
          );
          store.dispatch(
            setBuiltFormEditorRoute(BUILT_FORM_TYPES.BUILDING, namespace, key),
          );
        }
      } else if (newExploreUIProperties.settingsViewVisible) {
        store.dispatch(
          setSettingsRoute(newExploreUIProperties.settingsViewVisible),
        );
      } else if (newExploreUIProperties.profileViewVisible) {
        store.dispatch(setProfileRoute());
      } else if (
        !newExploreUIProperties.exploreViewVisible &&
        !newExploreUIProperties.reportViewVisible
      ) {
        const { namespace, key } = parseFullPath(newActiveProjectId, 'project');
        store.dispatch(setExploreNewRoute(namespace, key, true, false, mode));
      }
    }),
  );
  return unsubscribe;
};
